import iconStorniert from '../../assets/icon-bestellung-storniert.svg';
import iconEingangDebitor from '../../assets/icon-bestellung-eingang-debitor.svg';
import iconEingangDTC from '../../assets/icon-bestellung-eingang-dtc.svg';
import { extractDateAndTimeFromDateString, getISOWunschtermin } from './date-helper';
import { ArtikelTyp, Berechnungsart, BestellungGesamtstatus } from '../types/enums';
import { emptyArtikelLoseWare, emptyArtikelSackware } from '../types/defaultValues';
import type { TourenplanForm, TourenplanLoseWareBestellung, TourenplanSackwareBestellung } from '../../views/Tourenplan/TourenplanTypes';
import { NEWID_PREFIX } from '../constants';
import type { ArtikelZulageBackendItem } from '../types/bestellung';
import type {
    ArtikelLoseWare,
    ArtikelSackware,
    BaseBestellposition,
    BestellpositionArtikelLoseWare,
    BestellpositionArtikelLoseWareView,
    BestellpositionArtikelSackware,
    BestellpositionArtikelSackwareView,
    BestellpositionDetails,
    BestellpositionDetailsLoseWare,
    BestellpositionDetailsSackware,
    BestellpositionView,
    BestellungDetails,
} from '../types';
import dayjs from 'dayjs';
import { COMPANY_NAME_DATIVE } from '../constants/company';

export const isStornierbarePosition = (bestellposition: BestellpositionView | Partial<BestellpositionView>): boolean => {
    return typeof bestellposition.storno !== 'undefined' && !bestellposition.storno;
};

export const isExistingBestellposition = (bestellposition: BestellpositionView | Partial<BestellpositionView>): boolean => {
    return typeof bestellposition.storno !== 'undefined' && typeof bestellposition.positionsnummer !== 'undefined';
};

export const isLoseWare = (
    bestellposition: BestellpositionView | Partial<BestellpositionView>
): bestellposition is BestellpositionArtikelLoseWareView => {
    return bestellposition.artikelTyp === ArtikelTyp.LoseWare;
};

export const isLoseWareDetails = (bestellposition: BestellpositionDetails): bestellposition is BestellpositionDetailsLoseWare => {
    return bestellposition.artikelTyp === ArtikelTyp.LoseWare;
};

export const isSackwareDetails = (bestellposition: BestellpositionDetails): bestellposition is BestellpositionDetailsSackware => {
    return bestellposition.artikelTyp === ArtikelTyp.Sackware;
};

export const isSackwareView = (
    bestellposition: BestellpositionView | Partial<BestellpositionView>
): bestellposition is BestellpositionArtikelSackwareView => {
    return bestellposition.artikelTyp === ArtikelTyp.Sackware;
};

export const isSackware = (bestellposition: BaseBestellposition): bestellposition is BestellpositionArtikelSackware => {
    return bestellposition.artikelTyp === ArtikelTyp.Sackware;
};

export const hasAenderbarBisExpiredForBestellposition = (bestellposition: BestellpositionArtikelLoseWareView): boolean => {
    return dayjs(bestellposition.aenderbarBis).isBefore(dayjs());
};

export const loseWareMengePattern = /^([1-9][0-9]*(,\d)?|0,\d)$/;
export const sackwareMengePattern = /^\d*$/;

export const convertLoseWareMengeToNumber = (menge: string): number => {
    if (menge === '') {
        return 0;
    }

    const mengeAsNumber = Number(menge.replace(',', '.'));
    if (isNaN(mengeAsNumber)) {
        throw new Error(`Provided lose Ware menge string ${menge} cannot be parsed`);
    }
    return mengeAsNumber;
};

export const convertSackwareMengeToNumber = (menge: string): number => {
    if (menge === '') {
        return 0;
    }

    const mengeAsNumber = Number(menge);
    if (isNaN(mengeAsNumber)) {
        throw new Error(`Provided Sackware menge string ${menge} cannot be parsed`);
    }
    return mengeAsNumber;
};

export const convertMengeToString = (menge: number | string): string => {
    if (!menge) {
        return '';
    }

    return menge.toString().replace('.', ',');
};

export const addIndexToBestellposition = (bestellposition: BestellpositionView, index: number): BestellpositionView => {
    return {
        ...bestellposition,
        index,
    };
};

export const bestellpositionenViewFromBestellpositionenAndArtikeln = (
    bestellpositionen: BaseBestellposition[],
    artikelSackwareList: ArtikelSackware[],
    artikelLoseWareList: ArtikelLoseWare[]
): BestellpositionView[] => {
    return bestellpositionen.map((bp, index) => {
        if (isSackwareView(bp)) {
            const artikel = artikelSackwareList.find((a) => bp.artikelNummer === a.nummer) || emptyArtikelSackware;
            return {
                ...(artikel || emptyArtikelSackware),
                mengeVe: bp ? bp.mengeVe : 0,
                mengePal: bp ? bp.mengePal : 0,
                silo: bp ? bp.silo : 0,
                werkId: bp.werkId || '',
                aufPalRunden: false,
                lagerKapazitaetVE: undefined,
                bestandVE: undefined,
                index,
            };
        }
        // API liefert BestellpositionDetails mit "nummer", lokale State-Objekte haben ggf. "artikelNummer"
        const artikelNummer = (bp as { nummer?: string }).nummer ?? bp.artikelNummer;
        const artikel = artikelLoseWareList.find((a) => artikelNummer === a.nummer) || emptyArtikelLoseWare;
        const bpLw = bp as BestellpositionArtikelLoseWare & {
            artikelzulage?: ArtikelZulageBackendItem[];
            artikelZulage?: ArtikelZulageBackendItem[];
        };
        const isNewArtikel = (artikelNummer ?? '').startsWith(NEWID_PREFIX);
        // Beim Zurücknavigieren: zulagenData aus gespeicherter Position wiederherstellen, damit Artikelzulage/Zulage im Formular sichtbar bleibt
        const artikelzulageFromBp = bpLw.artikelzulage ?? bpLw.artikelZulage;
        const hasArtikelzulagen = Array.isArray(artikelzulageFromBp) && artikelzulageFromBp.length > 0;
        const firstAz = hasArtikelzulagen && artikelzulageFromBp ? artikelzulageFromBp[0] : null;
        const zulageTextWert = bpLw.zulageText || bpLw.sonderherstellungszulage || (firstAz && (firstAz.bezeichnung ?? firstAz.name)) || '';
        const zulageIdWert = bpLw.zulageId ?? (firstAz && (firstAz.code ?? firstAz.artikelZulageCode)) ?? undefined;
        const dosierwertWert = firstAz?.dosierung;
        const zulagenData =
            hasArtikelzulagen && artikelzulageFromBp
                ? {
                      zulagen: artikelzulageFromBp.map((z) => ({
                          zulageId: z.code ?? z.artikelZulageCode,
                          freitext: z.bezeichnung ?? z.name ?? '',
                          dosierwert: z.dosierung,
                      })),
                  }
                : bpLw.zulage && (zulageTextWert || zulageIdWert)
                ? {
                      zulagen: [{ zulageId: zulageIdWert, freitext: zulageTextWert || '', dosierwert: dosierwertWert }],
                  }
                : undefined;
        // Für ZulageDisplayTableRow/ZulageDisplayList: zulagen-Array (wird in Bestellansicht angezeigt)
        const zulagenDisplay =
            hasArtikelzulagen && artikelzulageFromBp ? artikelzulageFromBp : bpLw.zulage && zulageTextWert ? [zulageTextWert] : undefined;
        return {
            ...(artikel || emptyArtikelLoseWare),
            bezeichnung: isNewArtikel && bpLw.bezeichnung ? bpLw.bezeichnung : artikel.bezeichnung,
            nummer: isNewArtikel ? artikelNummer : artikel.nummer,
            menge: bp ? bpLw.menge.toString().replace('.', ',') : '0',
            werkId: bp.werkId || '',
            silo: bpLw.silo || '',
            zulage: bpLw.zulage || hasArtikelzulagen || false,
            zulageText: zulageTextWert,
            zulageId: zulageIdWert,
            zulagenData,
            zulagen: zulagenDisplay,
            berechnungsart: bpLw.berechnungsart || Berechnungsart.KeinVorgabe,
            kontrakt: bpLw.kontrakt,
            index,
        };
    });
};

export const bestellpositionenViewFromBestellpositionenDetailsAndArtikeln = (
    bestellpositionen: BestellpositionDetails[],
    artikelSackwareList: ArtikelSackware[],
    artikelLoseWareList: ArtikelLoseWare[]
): BestellpositionView[] => {
    return bestellpositionen.map((position, index) => {
        if (isLoseWareDetails(position)) {
            const dateAndTimeFromDateString = extractDateAndTimeFromDateString(position.wunschtermin);
            const artikel = artikelLoseWareList.find((a) => position.nummer === a.nummer) || emptyArtikelLoseWare;
            const isNewArtikel = position.nummer.startsWith(NEWID_PREFIX);

            // Backend kann artikelzulage oder artikelZulage (camelCase) liefern; ggf. als Einzelobjekt statt Array
            const posLw = position as BestellpositionDetailsLoseWare & {
                artikelZulage?: ArtikelZulageBackendItem[] | ArtikelZulageBackendItem;
            };
            const raw = posLw.artikelzulage ?? posLw.artikelZulage;
            const artikelzulageFromBackend: ArtikelZulageBackendItem[] | undefined = Array.isArray(raw)
                ? raw
                : raw && typeof raw === 'object'
                ? [raw as ArtikelZulageBackendItem]
                : undefined;
            const hasArtikelzulagen = Array.isArray(artikelzulageFromBackend) && artikelzulageFromBackend.length > 0;
            const firstArtikelzulage = hasArtikelzulagen && artikelzulageFromBackend ? artikelzulageFromBackend[0] : null;
            const zulageWert = position.zulage || position.sonderherstellung || hasArtikelzulagen || false;
            const zulageTextWert =
                position.zulageText ||
                position.sonderherstellungszulage ||
                (firstArtikelzulage && (firstArtikelzulage.bezeichnung ?? firstArtikelzulage.name)) ||
                '';
            const zulageIdWert =
                position.zulageId ?? (firstArtikelzulage && (firstArtikelzulage.code ?? firstArtikelzulage.artikelZulageCode)) ?? undefined;
            const dosierwertWert = firstArtikelzulage?.dosierung;
            // Für Display-Komponenten: zulagen-Array (artikelzulage vom Backend oder Freitext als String)
            const zulagenDisplay =
                hasArtikelzulagen && artikelzulageFromBackend
                    ? artikelzulageFromBackend
                    : zulageWert && zulageTextWert
                    ? [zulageTextWert]
                    : undefined;

            const zulagenData =
                hasArtikelzulagen && artikelzulageFromBackend
                    ? {
                          zulagen: artikelzulageFromBackend.map((z) => ({
                              zulageId: z.code ?? z.artikelZulageCode,
                              freitext: z.bezeichnung ?? z.name ?? '',
                              dosierwert: z.dosierung,
                          })),
                      }
                    : zulageWert && (zulageTextWert || zulageIdWert)
                    ? {
                          zulagen: [{ zulageId: zulageIdWert, freitext: zulageTextWert || '', dosierwert: dosierwertWert }],
                      }
                    : undefined;

            const bestellpositionLoseWareView: BestellpositionArtikelLoseWareView = {
                ...artikel,
                index: index,
                bezeichnung: isNewArtikel && position.bezeichnung ? position.bezeichnung : artikel.bezeichnung,
                nummer: isNewArtikel ? position.nummer : artikel.nummer,
                positionsnummer: position.positionsnummer,
                menge: position.menge.toString().replace('.', ',') || '0',
                werkId: position.werkId || '',
                silo: position.silo || '',
                zulage: zulageWert,
                zulageText: zulageTextWert,
                zulageId: zulageIdWert,
                dosierwert: dosierwertWert,
                zulagenData,
                zulagen: zulagenDisplay,
                berechnungsart: position.berechnungsart || Berechnungsart.KeinVorgabe,
                kontrakt: position.kontrakt,
                storno: position.storno,
                aenderbarBis: position.aenderbarBis,
                wunschtermin: position.wunschtermin,
                debitorDatum: dateAndTimeFromDateString?.datum,
                debitorUhrzeit: dateAndTimeFromDateString?.uhrzeit,
                liefer_status: position.liefer_status,
                erpStatus: position.erpStatus,
            };
            return bestellpositionLoseWareView;
        }

        const artikel = artikelSackwareList.find((a) => position.nummer === a.nummer) || emptyArtikelSackware;
        const bestellpositionSackwareView: BestellpositionArtikelSackwareView = {
            ...artikel,
            index: index,
            positionsnummer: position.positionsnummer,
            mengeVe: position.mengeVe,
            mengePal: position.mengePal,
            silo: position.silo,
            werkId: position.werkId,
            aufPalRunden: false,
            lagerKapazitaetVE: undefined,
            bestandVE: undefined,
            storno: position.storno,
            liefer_status: position.liefer_status,
            erpStatus: position.erpStatus,
        };
        return bestellpositionSackwareView;
    });
};

export const onlyBestellpositionenWithMenge = (bestellPosition: Partial<BestellpositionView>): boolean => {
    if (bestellPosition.artikelTyp === ArtikelTyp.Sackware) {
        const bvSw = bestellPosition as BestellpositionArtikelSackwareView;
        const mengeVe = typeof bvSw.mengeVe === 'number' ? bvSw.mengeVe : parseInt(String(bvSw.mengeVe || '0'), 10);
        const mengePal = typeof bvSw.mengePal === 'number' ? bvSw.mengePal : parseInt(String(bvSw.mengePal || '0'), 10);
        return mengeVe > 0 || mengePal > 0;
    } else {
        const bvLW = bestellPosition as BestellpositionArtikelLoseWareView;
        const menge = bvLW.menge || '0';
        return parseFloat(menge.toString().replace(',', '.')) > 0;
    }
};

/** Filtert Bestellpositionen (API-Format) nach Menge – für den Versand an die API (nur Positionen mit Menge mitsenden). */
export const onlyBestellpositionenWithMengeFromApi = (bp: BaseBestellposition): boolean => {
    if (bp.artikelTyp === ArtikelTyp.Sackware) {
        const sw = bp as BestellpositionArtikelSackware;
        const mengeVe = typeof sw.mengeVe === 'number' ? sw.mengeVe : parseInt(String(sw.mengeVe || '0'), 10);
        const mengePal = typeof sw.mengePal === 'number' ? sw.mengePal : parseInt(String(sw.mengePal || '0'), 10);
        return mengeVe > 0 || mengePal > 0;
    }
    const lw = bp as BestellpositionArtikelLoseWare;
    const menge = lw.menge ?? '0';
    return parseFloat(String(menge).replace(',', '.')) > 0;
};

export const bestellpositionDetailsToBestellpositionView = (bestellpositionen: BestellpositionDetails[]): BestellpositionView[] => {
    return bestellpositionen.map((bestellposition, index) => {
        if (isSackwareDetails(bestellposition)) {
            return {
                ...bestellposition,
                bezeichnung: bestellposition.bezeichnung ?? '',
                aufPalRunden: false,
                lagerKapazitaetVE: undefined,
                bestandVE: undefined,
                index,
            };
        }
        return {
            ...bestellposition,
            bezeichnung: bestellposition?.bezeichnung ?? '',
            index,
        };
    });
};

export const mapBestellpositionenDetailsToBestellpositionen = (
    bestellposition: BestellpositionDetails
): BestellpositionArtikelSackware | BestellpositionArtikelLoseWare => {
    if (isSackwareDetails(bestellposition)) {
        return {
            ...bestellposition,
            artikelNummer: bestellposition.nummer,
        };
    }
    const pos = bestellposition as BestellpositionDetailsLoseWare;
    const bezeichnung = pos.nummer.startsWith(NEWID_PREFIX) ? { bezeichnung: pos.bezeichnung } : {};
    const hasArtikelzulagen = Array.isArray(pos.artikelzulage) && pos.artikelzulage.length > 0;

    const base: BestellpositionArtikelLoseWare = {
        ...pos,
        ...bezeichnung,
        artikelNummer: pos.nummer,
        zulageText: pos.zulageText || '',
        zulageId: pos.zulageId,
        kontrakt: pos.berechnungsart === Berechnungsart.Kontrakt ? pos.kontrakt : undefined,
    };

    if (hasArtikelzulagen) {
        return { ...base, sonderherstellung: false, artikelzulage: pos.artikelzulage, sonderherstellungszulage: undefined };
    }
    if (pos.sonderherstellung || (pos.zulage && pos.sonderherstellungszulage)) {
        return {
            ...base,
            sonderherstellung: true,
            sonderherstellungszulage: pos.sonderherstellungszulage || pos.zulageText || '',
            artikelzulage: undefined,
        };
    }
    return { ...base, sonderherstellung: false };
};

/**
 * Optionen für das Mapping von View zu Request.
 * - onlySonderherstellung: true = Desktop-Ansicht (nur Freitext) → immer Sonderherstellung senden, nie Artikelzulage.
 * - onlySonderherstellung: false/undefined = Mobile App → Artikelzulage (Dropdown) oder Sonderherstellung (Freitext) je nach Eingabe.
 */
export type MapBestellpositionenViewToBestellpositionenOptions = {
    onlySonderherstellung?: boolean;
};

/**
 * Mappt View → Backend-Payload für eine Bestellposition.
 * Lose-Ware: Zwei getrennte Zulage-Arten (gegenseitig exklusiv):
 * - Sonderherstellung = Freitext → ein String (sonderherstellungszulage).
 * - Artikelzulagen = vom Dropdown → ein Objekt oder Liste von Objekten (artikelzulage: { artikelZulageCode, name, dosierung }[]).
 */
export const mapBestellpositionenViewToBestellpositionen = (
    bestellposition: BestellpositionView,
    options?: MapBestellpositionenViewToBestellpositionenOptions
): BestellpositionArtikelSackware | BestellpositionArtikelLoseWare => {
    if (isSackwareView(bestellposition)) {
        return {
            positionsnummer: bestellposition.positionsnummer,
            artikelNummer: bestellposition.nummer,
            artikelTyp: ArtikelTyp.Sackware,
            mengeVe: typeof bestellposition.mengeVe === 'string' ? 0 : bestellposition.mengeVe,
            mengePal: typeof bestellposition.mengePal === 'string' ? 0 : bestellposition.mengePal,
            silo: typeof bestellposition.silo === 'string' ? 0 : bestellposition.silo,
            werkId: bestellposition.werkId,
            storno: bestellposition.storno,
            index: bestellposition.index,
        };
    }
    const bezeichnung = bestellposition.nummer.startsWith(NEWID_PREFIX) ? { bezeichnung: bestellposition.bezeichnung } : {};
    const onlySonderherstellung = options?.onlySonderherstellung === true;

    const viewLw = bestellposition as BestellpositionArtikelLoseWareView;
    const hasZulage = !!bestellposition.zulage;
    const zulagenData = viewLw.zulagenData?.zulagen;

    // Strukturierte Artikelzulagen aus zulagenData (Dropdown) immer zuerst mappen – unabhängig von onlySonderherstellung,
    // sonst gehen Codes/Dosierungen bei Web-Bestellbearbeitung verloren und werden fälschlich als Sonderherstellung gesendet.
    // onlySonderherstellung steuert nur noch den Fallback (Freitext/Legacy ohne zulagenData-Zeilen mit Code).
    if (hasZulage && Array.isArray(zulagenData) && zulagenData.length > 0) {
        // Nur echte Artikelzulagen (Dropdown-Code). Reiner Freitext → unten Sonderherstellung, nie artikelzulage.
        const artikelzulage = zulagenData
            .filter((e) => e.zulageId != null && String(e.zulageId).trim() !== '')
            .map((e) => ({
                artikelZulageCode: String(e.zulageId).trim(),
                name: e.freitext != null ? String(e.freitext).trim() : '',
                dosierung: e.dosierwert,
            }));
        if (artikelzulage.length > 0) {
            return {
                ...bezeichnung,
                positionsnummer: bestellposition.positionsnummer,
                artikelNummer: bestellposition.nummer,
                artikelTyp: ArtikelTyp.LoseWare,
                menge: parseFloat(bestellposition.menge.toString().replace(',', '.')),
                werkId: bestellposition.werkId,
                storno: bestellposition.storno,
                silo: bestellposition.silo,
                zulage: true,
                zulageText: artikelzulage
                    .map((z) => z.name)
                    .filter(Boolean)
                    .join(', '),
                zulageId: artikelzulage[0]?.artikelZulageCode,
                berechnungsart: bestellposition.berechnungsart,
                kontrakt: bestellposition.berechnungsart === Berechnungsart.Kontrakt ? bestellposition.kontrakt : undefined,
                index: bestellposition.index,
                wunschtermin: bestellposition.wunschtermin,
                sonderherstellung: false,
                sonderherstellungszulage: undefined,
                artikelzulage,
            };
        }
    }

    const zulageTextFromData =
        Array.isArray(zulagenData) && zulagenData.length > 0
            ? zulagenData
                  .map((e) => e.freitext?.trim() ?? '')
                  .filter(Boolean)
                  .join(', ')
            : '';
    const zulageText = hasZulage ? bestellposition.zulageText?.trim() || zulageTextFromData || '' : '';
    const zulageId = onlySonderherstellung ? undefined : hasZulage ? bestellposition.zulageId : undefined;
    const dosierwert = viewLw.dosierwert;

    const isArtikelzulage = !onlySonderherstellung && hasZulage && !!zulageId;
    const isSonderherstellung = hasZulage && (onlySonderherstellung || !zulageId);

    const baseResult: BestellpositionArtikelLoseWare = {
        ...bezeichnung,
        positionsnummer: bestellposition.positionsnummer,
        artikelNummer: bestellposition.nummer,
        artikelTyp: ArtikelTyp.LoseWare,
        menge: parseFloat(bestellposition.menge.toString().replace(',', '.')),
        werkId: bestellposition.werkId,
        storno: bestellposition.storno,
        silo: bestellposition.silo,
        zulage: bestellposition.zulage,
        zulageText,
        zulageId,
        berechnungsart: bestellposition.berechnungsart,
        kontrakt: bestellposition.berechnungsart === Berechnungsart.Kontrakt ? bestellposition.kontrakt : undefined,
        index: bestellposition.index,
        wunschtermin: bestellposition.wunschtermin,
    };

    if (isArtikelzulage) {
        // Einzelne Artikelzulage (Objekt) – keine Sonderherstellung mitsenden.
        return {
            ...baseResult,
            sonderherstellung: false,
            sonderherstellungszulage: undefined,
            artikelzulage: [{ artikelZulageCode: zulageId as string, name: zulageText || '', dosierung: dosierwert }],
        };
    }
    if (isSonderherstellung) {
        // Nur Freitext (String) – keine Artikelzulagen-Objekte mitsenden.
        return {
            ...baseResult,
            sonderherstellung: true,
            sonderherstellungszulage: zulageText,
            artikelzulage: undefined,
        };
    }
    return {
        ...baseResult,
        sonderherstellung: false,
        artikelzulage: undefined,
    };
};

/**
 * Formatiert Bestellpositionen für die API.
 * Lose-Ware: API erwartet "artikelZulage" (camelCase) für Dropdown-Zulagen (Liste von Objekten).
 * Sonderherstellung bleibt "sonderherstellungszulage" (String). Beides nie gleichzeitig pro Position.
 */
export const bestellpositionenToBackendFormat = <T extends { artikelTyp?: string; artikelzulage?: unknown }>(
    bestellpositionen: T[]
): T[] => {
    return bestellpositionen.map((pos) => {
        if (pos.artikelTyp !== ArtikelTyp.LoseWare) return pos;
        const { artikelzulage, ...rest } = pos as T & { artikelzulage?: unknown };
        if (artikelzulage === undefined) return pos;
        return { ...rest, artikelZulage: artikelzulage } as unknown as T;
    });
};

export const mapDateAndTimeToWunschterminForPosition = (bestellposition: BestellpositionView): BestellpositionView => {
    bestellposition.wunschtermin = getISOWunschtermin(bestellposition.debitorDatum, bestellposition.debitorUhrzeit);
    return bestellposition;
};

export const mengeStringFromBestellpositionView = (bestellposition: BestellpositionDetails): string => {
    if (isSackwareDetails(bestellposition)) {
        const mengePal = bestellposition.mengePal && bestellposition.mengePal !== 0 ? `${bestellposition.mengePal} Pal` : '';
        const mengeVe = bestellposition.mengeVe && bestellposition.mengeVe !== 0 ? `${bestellposition.mengeVe} Ve` : '';
        return [mengePal, mengeVe].filter(Boolean).join(', ');
    }
    return bestellposition.menge ? `${convertMengeToString(bestellposition.menge)} t` : '';
};

export const statusToIcon = (status: BestellungGesamtstatus): string => {
    switch (status) {
        case BestellungGesamtstatus.STORNIERT_DURCH_LANDWARENHANDEL:
            return iconStorniert;
        case BestellungGesamtstatus.STORNIERT_DURCH_WARENEMPFAENGER:
            return iconStorniert;
        case BestellungGesamtstatus.LANDWARENHANDEL:
            return iconEingangDebitor;
        case BestellungGesamtstatus.DTC:
            return iconEingangDTC;
        case BestellungGesamtstatus.AENDERUNGSWUNSCH:
            return iconEingangDebitor;
        default:
            return 'X';
    }
};

export const statusToText = (status: BestellungGesamtstatus): string => {
    switch (status) {
        case BestellungGesamtstatus.STORNIERT_DURCH_LANDWARENHANDEL:
            return 'Storniert durch Landwarenhandel';
        case BestellungGesamtstatus.STORNIERT_DURCH_WARENEMPFAENGER:
            return 'Storniert durch Warenempfänger';
        case BestellungGesamtstatus.LANDWARENHANDEL:
            return 'Beim Landwarenhandel eingegangen';
        case BestellungGesamtstatus.DTC:
            return `Bei ${COMPANY_NAME_DATIVE} eingegangen`;
        case BestellungGesamtstatus.AENDERUNGSWUNSCH:
            return 'Änderungswunsch an DTC übermittelt';
        case BestellungGesamtstatus.NONE:
            return '';
    }
};

const createFormInputsMap = <T extends TourenplanLoseWareBestellung | TourenplanSackwareBestellung>(
    tourenplanBestellungen: T[],
    bestellnummer: string
): Map<string, T> => {
    const formInputsMap = new Map<string, T>();
    tourenplanBestellungen
        ?.filter((bestellungInForm) => bestellungInForm.bestellnummer === bestellnummer)
        .forEach((currentBestellungInForm) => {
            formInputsMap.set(`${bestellnummer}-${currentBestellungInForm.nummer}`, currentBestellungInForm);
        });
    return formInputsMap;
};

const getFormInputFromMap = <T extends TourenplanLoseWareBestellung | TourenplanSackwareBestellung>(
    map: Map<string, T>,
    bestellnummer: string,
    artikelnummer: string
): T => {
    const key = `${bestellnummer}-${artikelnummer}`;
    const value = map.get(key);
    if (value) {
        return value;
    } else {
        throw new Error(`form values missing: Bestellnummer: ${bestellnummer}, Artikelnummer: ${artikelnummer}`);
    }
};

export const mapBestellungenToTourenplanLoseWareBestellungen = (bestellungen: BestellungDetails[]): TourenplanLoseWareBestellung[] => {
    return bestellungen.flatMap((bestellung) => {
        return bestellung.bestellpositionen.filter(isLoseWareDetails).map((position) => {
            const { menge, nummer, bezeichnung, silo, werk, werkId, zulage, zulageText } = position;
            const { bestellnummer, lieferhinweis, warenempfaenger, rechnungsempfaenger } = bestellung;
            const tourenplanBestellung: TourenplanLoseWareBestellung = {
                bestellnummer,
                lieferhinweis,
                warenempfaenger,
                menge: convertMengeToString(menge),
                nummer,
                bezeichnung,
                partnerNummer: rechnungsempfaenger.partnerNummer,
                silo,
                werk,
                werkId,
                zulage,
                zulageText,
                kesselverteilung: [],
            };

            return tourenplanBestellung;
        });
    });
};

export const mapBestellungenToTourenplanSackwareBestellungen = (bestellungen: BestellungDetails[]): TourenplanSackwareBestellung[] => {
    return bestellungen.flatMap((bestellung) => {
        return bestellung.bestellpositionen.filter(isSackwareDetails).map((position) => {
            const { mengeVe, mengePal, faktorBasiseinheitPal, faktorBasiseinheitVe, nummer, bezeichnung, werk, werkId } = position;
            const { bestellnummer, lieferhinweis, warenempfaenger, rechnungsempfaenger } = bestellung;

            const tourenplanBestellung: TourenplanSackwareBestellung = {
                bestellnummer,
                lieferhinweis,
                warenempfaenger,
                mengeVe: convertMengeToString(mengeVe),
                mengePal: convertMengeToString(mengePal),
                faktorBasiseinheitPal,
                faktorBasiseinheitVe,
                partnerNummer: rechnungsempfaenger.partnerNummer,
                nummer,
                bezeichnung,
                werk,
                werkId,
            };

            return tourenplanBestellung;
        });
    });
};

export const mergeTourenplanFormIntoBestellungen = (
    tourenplanFormular: TourenplanForm,
    currentSammelbestellung: BestellungDetails[]
): BestellungDetails[] => {
    const formularDatum = tourenplanFormular.datumDebitor;
    const formularUhrzeit = tourenplanFormular.uhrzeitDebitor;
    const wunschtermin = getISOWunschtermin(formularDatum, formularUhrzeit) ?? '';
    const formularKfzKennzeichen = tourenplanFormular.kfzKennzeichen;
    const formularSpedition = tourenplanFormular.spedition;
    const formularLieferbedingung = tourenplanFormular.lieferbedingung;

    return currentSammelbestellung.map((bestellung) => {
        const { bestellnummer, bestellpositionen } = bestellung;
        const loseWareFormInputs = createFormInputsMap(tourenplanFormular.loseWareBestellungen, bestellnummer);
        const sackwareFormInputs = createFormInputsMap(tourenplanFormular.sackwareBestellungen, bestellnummer);
        const updatedBestellpositionen: BestellpositionDetails[] = bestellpositionen.map((bestellposition) => {
            const { nummer: artikelnummer } = bestellposition;

            if (isLoseWareDetails(bestellposition)) {
                const { menge, silo, werkId, werk } = getFormInputFromMap(loseWareFormInputs, bestellnummer, artikelnummer);
                return {
                    ...bestellposition,
                    werkId,
                    werk,
                    menge: convertLoseWareMengeToNumber(menge),
                    silo,
                };
            } else {
                const { mengePal, mengeVe, werkId, werk } = getFormInputFromMap(sackwareFormInputs, bestellnummer, artikelnummer);
                return {
                    ...bestellposition,
                    mengePal: convertSackwareMengeToNumber(mengePal),
                    mengeVe: convertSackwareMengeToNumber(mengeVe),
                    werkId,
                    werk,
                };
            }
        });

        return {
            ...bestellung,
            wunschtermin,
            kfzKennzeichen: formularKfzKennzeichen,
            spedition: formularSpedition,
            lieferbedingung: formularLieferbedingung,
            bestellpositionen: updatedBestellpositionen,
        };
    });
};

export const replaceDotsWithCommas = (e: React.FormEvent<HTMLInputElement>): void => {
    const input = e.currentTarget;
    input.value = input.value.replace(/\./g, ',');
};
